/**
 * Shared scorecard components used by:
 *   - N2ScorecardPage        (overview)
 *   - N2ScorecardModelPage   (per-model deep-dive)
 *   - N2ScorecardLogPage     (full audit log)
 *
 * Extracted so all three pages render identical bands and cells without
 * duplication. Data is provided by useNotchesScorecard() in each page.
 */

import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { palette } from './theme';
import { SectionLabel, Body } from './N2Primitives';
import {
  ScorecardAggregate, ScorecardOutcome, ScorecardModel,
  ScorecardPayload, UnifiedRow,
} from './notchesScorecardService';
import { implicationFor } from './notchTradeImplication';
import {
  detectConfluence, isConfluenceMember,
  ConfluenceEvent, ConfluenceMember, CONFLUENCE_WINDOW_DAYS,
} from './notchConfluence';
import { buildDailyRead, DailyReadEntry } from './dailyRead';

export type ModelFilter = 'All' | ScorecardModel;
export type OutcomeFilter = 'all' | ScorecardOutcome;

const MODEL_FILTERS: ModelFilter[] = ['All', 'COT', 'GEX', 'IV', 'AVWAP'];
const OUTCOME_FILTERS: OutcomeFilter[] = ['all', 'hit', 'miss', 'open', 'neutral'];

// ---- Scorecard sub-nav (appears on all /n2/scorecard* pages) -------------

const SUB_NAV_LINKS: { to: string; label: string }[] = [
  { to: '/n2/scorecard',          label: 'Overview' },
  { to: '/n2/scorecard/playbook', label: 'Playbook' },
  { to: '/n2/scorecard/cot',      label: 'COT' },
  { to: '/n2/scorecard/gex',      label: 'GEX' },
  { to: '/n2/scorecard/iv',       label: 'IV' },
  { to: '/n2/scorecard/avwap',    label: 'AVWAP' },
  { to: '/n2/scorecard/log',      label: 'Full log' },
];

export const ScorecardSubNav: React.FC = () => {
  const loc = useLocation();
  return (
    <div style={{
      display: 'flex', gap: 20, flexWrap: 'wrap',
      padding: '12px 0 14px', borderBottom: `1px solid ${palette.line}`,
      marginBottom: 8,
    }}>
      {SUB_NAV_LINKS.map((l) => {
        const active = loc.pathname === l.to;
        return (
          <Link key={l.to} to={l.to} style={{
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: active ? palette.ink : palette.ink3,
            textDecoration: 'none',
            borderBottom: active ? `2px solid ${palette.goldDeep}` : '2px solid transparent',
            paddingBottom: 4,
          }}>{l.label}</Link>
        );
      })}
    </div>
  );
};

// ---- Aggregate tile ------------------------------------------------------

export const AggTile: React.FC<{ agg: ScorecardAggregate }> = ({ agg }) => {
  const hitStr = agg.hitPct == null ? '—' : `${agg.hitPct.toFixed(1)}%`;
  const footer = agg.n === 0
    ? 'No resolved readings yet'
    : `n = ${agg.n}${agg.openN ? ` · ${agg.openN} in flight` : ''}`;
  return (
    <div style={{
      border: `1px solid ${palette.line}`, background: palette.paper,
      padding: '20px 22px 18px', borderRadius: 3,
    }}>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
      }}>{agg.label}</div>
      <div style={{
        fontFamily: 'var(--serif)', fontSize: 32, color: palette.ink,
        fontVariantNumeric: 'tabular-nums', marginTop: 8, lineHeight: 1,
      }}>{hitStr}</div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
        marginTop: 8, letterSpacing: '0.04em',
      }}>{footer}</div>
    </div>
  );
};

export const AggregateGrid: React.FC<{ aggregates: ScorecardAggregate[] }> = ({ aggregates }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${Math.min(aggregates.length, 4)}, 1fr)`,
    gap: 16,
  }} className="n2-agg">
    {aggregates.map((a) => <AggTile key={a.symbol} agg={a} />)}
  </div>
);

// ---- Reading log ---------------------------------------------------------

/**
 * ReadingLog — the sortable/filterable table.
 * When `pinnedModel` is provided the model filter is locked to that model
 * and the model column is hidden (per-model page use).
 */
export const ReadingLog: React.FC<{
  rows: UnifiedRow[];
  confluenceEvents: ConfluenceEvent[];
  pinnedModel?: ScorecardModel;
}> = ({ rows, confluenceEvents, pinnedModel }) => {
  const [modelFilter, setModelFilter] = useState<ModelFilter>(pinnedModel ?? 'All');
  const [outFilter, setOutFilter] = useState<OutcomeFilter>('all');

  const visible = useMemo(() => rows.filter(r =>
    (modelFilter === 'All' || r.model === modelFilter) &&
    (outFilter === 'all' || r.outcome === outFilter)
  ), [rows, modelFilter, outFilter]);

  return (
    <div style={{ marginTop: 56 }}>
      <SectionLabel left="Reading log" right={`${visible.length} shown of ${rows.length}`} />

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12,
        alignItems: 'center', margin: '20px 0 12px',
      }}>
        {!pinnedModel && (
          <FilterGroup label="Model" values={MODEL_FILTERS} active={modelFilter}
            onChange={(v) => setModelFilter(v as ModelFilter)} />
        )}
        <FilterGroup label="Outcome" values={OUTCOME_FILTERS} active={outFilter}
          onChange={(v) => setOutFilter(v as OutcomeFilter)} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontFamily: 'var(--sans)', fontSize: 14,
          fontVariantNumeric: 'tabular-nums',
        }}>
          <thead>
            <tr style={{ borderBottom: `1.5px solid ${palette.line}` }}>
              <Th w={110}>Date</Th>
              {!pinnedModel && <Th w={70}>Model</Th>}
              <Th w={80}>Symbol</Th>
              <Th>Reading</Th>
              <Th w={100}>Outcome</Th>
              <Th w={140}>Result</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={`${r.model}-${r.symbol}-${r.date}-${i}`}
                  style={{ borderBottom: `1px dashed ${palette.line}` }}>
                <Td style={{ color: palette.ink3, fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {r.date}
                </Td>
                {!pinnedModel && (
                  <Td><ModelChip model={r.model} /></Td>
                )}
                <Td>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 15, color: palette.ink }}>
                    {r.symbol}
                  </span>
                  {r.symbol !== r.tradable && (
                    <span style={{ marginLeft: 6, color: palette.ink3, fontSize: 12 }}>
                      · {r.tradable}
                    </span>
                  )}
                </Td>
                <Td style={{ color: palette.ink2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{r.reading}</span>
                    {isConfluenceMember(r, confluenceEvents) && <ConfluencePill />}
                  </div>
                  <ImplicationLine row={r} />
                </Td>
                <Td><OutcomeChip outcome={r.outcome} /></Td>
                <Td style={{
                  color:
                    r.displayColor === 'pos' ? palette.positive :
                    r.displayColor === 'neg' ? palette.negative :
                    palette.ink3,
                }}>{r.displayValue}</Td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><Td colSpan={pinnedModel ? 5 : 6} style={{ color: palette.ink3, padding: '32px 8px' }}>
                No readings match those filters.
              </Td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ImplicationLine: React.FC<{ row: UnifiedRow }> = ({ row }) => {
  const imp = implicationFor(row.model, row.tag, row.outcome);
  if (!imp.detail) return null;
  const isOpen = row.outcome === 'open';
  return (
    <div style={{
      marginTop: 4, fontFamily: 'var(--sans)',
      fontSize: 12, fontStyle: 'italic', lineHeight: 1.4,
      color: isOpen ? palette.ink2 : palette.ink3,
      fontWeight: isOpen ? 500 : 400,
    }}>
      {imp.detail}
    </div>
  );
};

// ---- Right-Now band ------------------------------------------------------

export const RightNowBand: React.FC<{ data: ScorecardPayload }> = ({ data }) => {
  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);

  interface OpenNotch {
    model: ScorecardModel;
    date: string;
    symbol: string;
    tag: string;
    reading: string;
    daysAgo: number;
    horizonDays: number;
  }

  const open: OpenNotch[] = [];
  const daysBetween = (a: string, b: string) => Math.floor(
    (new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86400000
  );

  for (const r of data.cot.rows)
    if (r.outcome === 'open') open.push({
      model: 'COT', date: r.date, symbol: r.symbol, tag: r.tag, reading: r.reading,
      daysAgo: daysBetween(r.date, todayIso), horizonDays: data.cot.meta.horizonDays,
    });
  for (const r of data.gex.rows)
    if (r.outcome === 'open') open.push({
      model: 'GEX', date: r.date, symbol: r.symbol, tag: r.tag, reading: r.reading,
      daysAgo: daysBetween(r.date, todayIso), horizonDays: data.gex.meta.forwardDays,
    });
  for (const r of data.iv.rows)
    if (r.outcome === 'open') open.push({
      model: 'IV', date: r.date, symbol: r.symbol, tag: r.tag, reading: r.reading,
      daysAgo: daysBetween(r.date, todayIso), horizonDays: data.iv.meta.windowDays,
    });
  for (const r of data.avwap.rows)
    if (r.outcome === 'open') open.push({
      model: 'AVWAP', date: r.date, symbol: r.symbol, tag: r.tag, reading: r.reading,
      daysAgo: daysBetween(r.date, todayIso), horizonDays: data.avwap.meta.forwardDays,
    });

  open.sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div style={{
      marginTop: 40, marginBottom: 16,
      padding: '28px 28px 24px',
      border: `1px solid ${palette.line}`,
      background: palette.paper2,
      borderRadius: 3,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        <div>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
          }}>Right now</span>
          <span style={{
            marginLeft: 12, fontFamily: 'var(--serif)', fontSize: 22, color: palette.ink,
          }}>
            {open.length === 0 ? 'No live readings' : `${open.length} live reading${open.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
          letterSpacing: '0.04em',
        }}>
          in flight · outcome pending
        </span>
      </div>

      {open.length === 0 && (
        <p style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
          margin: '12px 0 0', lineHeight: 1.5,
        }}>
          Nothing is currently mid-window across the four models. New readings
          typically fire when positioning, gamma regime, IV rank, or a swing-low
          test crosses a threshold — refresh to see them as they land.
        </p>
      )}

      {open.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(320px, 1fr))`,
          gap: 12, marginTop: 16,
        }}>
          {open.map((n) => <OpenNotchCard key={`${n.model}-${n.symbol}-${n.date}`} notch={n} />)}
        </div>
      )}
    </div>
  );
};

const OpenNotchCard: React.FC<{ notch: {
  model: ScorecardModel; date: string; symbol: string; tag: string;
  reading: string; daysAgo: number; horizonDays: number;
} }> = ({ notch }) => {
  const imp = implicationFor(notch.model, notch.tag, 'open');
  const daysLeft = Math.max(0, notch.horizonDays - notch.daysAgo);
  return (
    <div style={{
      border: `1px solid ${palette.line}`, background: palette.paper,
      padding: '16px 18px 14px', borderRadius: 3,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
      }}>
        <ModelChip model={notch.model} />
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 18, color: palette.ink,
        }}>{notch.symbol}</span>
        <span style={{
          marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11,
          color: palette.ink3,
        }}>
          day {notch.daysAgo}/{notch.horizonDays} · {daysLeft}d left
        </span>
      </div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2,
        marginBottom: 8, lineHeight: 1.4,
      }}>{notch.reading}</div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: palette.goldDeep,
        marginBottom: 6,
      }}>
        {imp.action}
      </div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 12, lineHeight: 1.4,
        color: palette.ink3, fontStyle: 'italic',
      }}>
        {imp.detail}
      </div>
    </div>
  );
};

// ---- Info tooltip primitive ("?" with browser title hover) ------------
// Matches the WFS pattern (title= attribute + cursor:help). Native browser
// tooltip — no library, no popover state, works on every platform.

// Model-specific tip lookups. Kept as pure functions so callers don't need to
// know the tip keys. TIP is defined below but JS hoisting handles this since
// these functions are called at runtime, not at module init.
export function modelExplainTip(model: ScorecardModel): string {
  if (model === 'COT')   return TIP.cotModel;
  if (model === 'GEX')   return TIP.gexModel;
  if (model === 'IV')    return TIP.ivModel;
  if (model === 'AVWAP') return TIP.avwapModel;
  return TIP.trackRecord;
}
export function modelTrackRecordTip(model: ScorecardModel): string {
  if (model === 'COT')   return TIP.cotTrackRecord;
  if (model === 'GEX')   return TIP.gexTrackRecord;
  if (model === 'IV')    return TIP.ivTrackRecord;
  if (model === 'AVWAP') return TIP.avwapTrackRecord;
  return TIP.trackRecord;
}

export const InfoTip: React.FC<{ tip: string }> = ({ tip }) => (
  <span
    role="img"
    aria-label={tip}
    title={tip}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 14, height: 14, marginLeft: 5,
      borderRadius: '50%',
      border: `1px solid ${palette.line}`,
      background: palette.paper,
      fontFamily: 'var(--sans)', fontSize: 9, fontWeight: 700,
      color: palette.ink3, cursor: 'help',
      verticalAlign: 'middle', lineHeight: 1,
    }}
  >?</span>
);

// Glossary — canonical explanation text for each ambiguous term. Kept as a
// module constant so the copy is edited in one place.
const TIP = {
  behindThisRead:
    'The individual model readings that produced the synthesis above. Each row shows one model firing on one symbol, with the model\'s public track record on that specific symbol.',
  dayCount:
    'How far into the model\'s outcome window this reading is. For example, day 2/5 means 2 days into a 5-day scoring window. When the window closes, the reading is scored hit or miss and added to the track record.',
  daysLeft:
    'Trading days remaining before this reading\'s outcome is scored. Once it hits day 0, the outcome resolves and posts to the reading log.',
  resolving:
    'This reading\'s outcome window closes today. Its hit-or-miss result posts to the log at day-end. Any trade a reader put on 5 days ago is completing its horizon now.',
  trackRecord:
    '"Correct" means the model\'s directional or vol call was right at the outcome window. Hit rate is measured on this specific model + symbol combination over the lookback window. Losing cells are shown alongside winning ones — that\'s the whole point of publishing the record.',
  // Per-model plain-English explanations. Model chips use these on hover;
  // the track-record proof line under each notch uses the matching per-model
  // version to explain what "correct" means for THAT model specifically.
  cotModel:
    'COT watches who\'s crowded. Every week the government publishes what the big money managers are doing. When one side gets extremely one-sided, the market often turns. LONG = big money is stretched long, model bets underlying goes DOWN in the next 4 weeks. SHORT = opposite.',
  cotTrackRecord:
    'For COT: after 4 weeks (28 days), did the close move in the bet direction? Any move counts as correct; any counter-move counts as wrong. No magnitude threshold. Low percentages are normal — 4 weeks is a long window and anything can happen (Fed, earnings, macro shocks). COT is best used to size DOWN when it disagrees with your other reads, size UP when it agrees.',
  gexModel:
    'GEX watches how bouncy the next week will be. Options dealers hedge every trade — their hedging either calms the market or amplifies moves, depending on their inventory. NEG_GEX = dealers will amplify moves (bet: next week more volatile). POS_GEX = dealers dampen moves (bet: next week quieter). No direction call — just size of moves.',
  gexTrackRecord:
    'For GEX: compare the average daily range over the next 5 trading days vs the prior 20-day average. NEG_GEX hits if forward range >= 115% of prior; POS_GEX hits if <= 85%. Everything in between is a miss. The 15% threshold is deliberately strict — most weeks hover near the recent average, so 30-40% hits is real signal (random would be ~30%). Use POS_GEX days to sell premium, NEG_GEX days for directional bets.',
  ivModel:
    'IV watches when option premium gets too high or too low. Implied volatility has a percentile rank against its own past year. When it\'s very high (>=70) or very low (<=30), it usually snaps back. HIGH = premium is fat, bet it falls back. LOW = premium is cheap, bet it rises back.',
  ivTrackRecord:
    'For IV: within 10 trading days of the crossing, does IVR cross back through 50? Yes = hit; full window elapsed with no cross-back = miss. Extreme regimes can persist (a market panic doesn\'t cure itself on schedule), so 40% hits is honest — sometimes the extreme just isn\'t done. Use HIGH to favor selling premium, LOW to favor buying. Don\'t use for entry timing — combine with other reads.',
  avwapModel:
    'AVWAP is the average price paid by everyone who bought since the last swing low. Institutions defend this level because losing it means all those buyers are now underwater, which triggers selling. That makes it a real support line. Reading fires when the underlying comes back down to test the line after being well above it.',
  avwapTrackRecord:
    'For AVWAP: 5 trading days after the retest, where is the close? Above AVWAP = hit (support held). More than 2% below = miss (support broke and confirmed). In between = neutral (muddy, doesn\'t count either way). AVWAP tests are near 50/50 by nature — support holds or breaks. Broken AVWAP often marks the start of a larger move down, so misses can be more valuable than hits.',
  exampleTrade:
    'One way a trader might act on this synthesis. Illustrative only — not personalized advice, not a specific recommendation. Structure, DTE, and delta ranges reflect what the model\'s outcome window supports.',
  illustrativeSizing:
    'How much of a trader\'s typical per-trade risk this example illustrates. 1× = normal size, 1.5× = higher conviction (aligned confluence lifts single-cell hit rates), 0.5× = smaller size (weak confidence or complex structure). Actual sizing is a trader\'s own risk-management decision.',
  notchesDesk:
    'Notches Desk is our authenticated research app. The desk shows per-symbol notches, model detail, and full audit for markets we cover. Notches Desk is research; trade execution happens at your own broker.',
  marketContext:
    'The scene: what regime this market is in right now, what the models collectively say about it, and why it\'s worth a look. Sets up the analysis that follows.',
  analysisSummary:
    'What our models say when read together. The synthesis paragraph is the top-line takeaway; the "Behind this read" rows show each model\'s specific firing plus its own track record on this symbol.',
  thingsToWatch:
    'The specific price levels, dates, and cross-checks that would confirm or invalidate this read as it plays out. Not a checklist to follow — items to keep an eye on so the reader knows when the setup has shifted.',
  plays:
    'One example structure a trader might consider based on this read. The example is illustrative — not a recommendation, and not the only way to express the view. Always educational.',
} as const;

// ---- This Week's Reads (per-market synthesis of live notches) ----------
// Top 3 by conviction rendered as hero cards; remaining shown as compact
// one-liners in an "other" section. Sector markets not covered by Notches
// Desk are flagged rather than hidden.

const NOTCHES_DESK_URL = 'https://www.wealthfinancialsystems.com/notches';

export const TodaysReadPanel: React.FC<{ data: ScorecardPayload }> = ({ data }) => {
  const entries = useMemo(() => buildDailyRead(data), [data]);
  const hero = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div style={{
      marginTop: 40, marginBottom: 16,
      padding: '28px 28px 24px',
      border: `1px solid ${palette.line}`,
      background: palette.paper,
      borderRadius: 3,
      boxShadow: `inset 3px 0 0 ${palette.ink}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        <div>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
          }}>This week</span>
          <span style={{
            marginLeft: 12, fontFamily: 'var(--serif)', fontSize: 22, color: palette.ink,
          }}>
            {entries.length === 0
              ? 'No live readings'
              : `${entries.length} market${entries.length > 1 ? 's' : ''} in play`}
          </span>
        </div>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
          letterSpacing: '0.04em',
        }}>
          <Link to="/n2/scorecard/playbook" style={{
            color: palette.goldDeep, textDecoration: 'none', fontWeight: 700,
          }}>how we build these reads →</Link>
        </span>
      </div>

      {entries.length === 0 && (
        <p style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
          margin: '12px 0 0', lineHeight: 1.6,
        }}>
          None of our models are mid-window right now. New readings publish
          when positioning, gamma regime, IV rank, or a support test crosses
          a threshold — days without readings are days to hold cash.
        </p>
      )}

      {hero.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18,
        }}>
          {hero.map((e) => <ReadHeroCard key={e.market} entry={e} />)}
        </div>
      )}

      {rest.length > 0 && <>
        <div style={{
          marginTop: 20, marginBottom: 10,
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
        }}>Other live reads</div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {rest.map((e) => <ReadCompactRow key={e.market} entry={e} />)}
        </div>
      </>}

      {entries.length > 0 && (
        <div style={{
          marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${palette.line}`,
          fontFamily: 'var(--sans)', fontSize: 11, lineHeight: 1.55,
          color: palette.ink3, fontStyle: 'italic',
        }}>
          For education only — not investment advice. Every example shows one
          way a reader might interpret the notches; sizing and risk management
          remain your responsibility. Notches is research, not execution — see
          {' '}<Link to="/n2/scorecard/playbook" style={{ color: palette.goldDeep, fontStyle: 'normal', textDecoration: 'none' }}>the playbook</Link>{' '}
          for the rules behind these reads.
        </div>
      )}
    </div>
  );
};

const ReadHeroCard: React.FC<{ entry: DailyReadEntry }> = ({ entry }) => {
  const accent = {
    bull: palette.positive,
    bear: palette.negative,
    mixed: palette.caution,
    neutral: palette.ink3,
  }[entry.accent];
  return (
    <div style={{
      border: `1px solid ${palette.line}`, background: palette.paper2,
      padding: '20px 22px 18px', borderRadius: 3,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 24, color: palette.ink,
        }}>{entry.market}</span>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          padding: '3px 10px', borderRadius: 2,
          background: `${accent}18`, color: accent,
          border: `1px solid ${accent}55`,
        }}>{entry.headline}</span>
      </div>

      {/* 1. MARKET CONTEXT */}
      <ReadSection label="Market Context" tip={TIP.marketContext}>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2, lineHeight: 1.6,
        }}>{entry.marketContext}</div>
      </ReadSection>

      {/* 2. ANALYSIS SUMMARY */}
      <ReadSection label="Analysis Summary" tip={TIP.analysisSummary}>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2, lineHeight: 1.6,
        }}>{entry.synthesis}</div>

        {/* Behind this read — the inline notch proof */}
        <div style={{ marginTop: 12 }}>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: palette.ink3,
            marginBottom: 8,
          }}>Behind this read<InfoTip tip={TIP.behindThisRead} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {entry.notches.map((n, i) => <NotchProofRow key={i} notch={n} />)}
          </div>
        </div>
      </ReadSection>

      {/* 3. THINGS TO WATCH */}
      <ReadSection label="Things to Watch" tip={TIP.thingsToWatch}>
        <ul style={{
          margin: 0, paddingLeft: 20,
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2, lineHeight: 1.6,
        }}>
          {entry.thingsToWatch.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
        </ul>
      </ReadSection>

      {/* 4. PLAYS */}
      <ReadSection label="Plays" tip={TIP.plays} accent="gold">
        <div style={{
          padding: '10px 14px',
          background: palette.paper, border: `1px dashed ${palette.line}`,
          borderRadius: 3,
        }}>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: palette.goldDeep, marginBottom: 4,
          }}>Example · illustrative<InfoTip tip={TIP.exampleTrade} /></div>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink, lineHeight: 1.55,
          }}>For example, {entry.primaryTrade}.</div>
          {entry.size && (
            <div style={{
              marginTop: 6, fontFamily: 'var(--sans)', fontSize: 11,
              color: palette.ink3, letterSpacing: '0.04em',
            }}>Illustrative sizing<InfoTip tip={TIP.illustrativeSizing} />: {entry.size}</div>
          )}
        </div>
      </ReadSection>

      <DeskCta covered={entry.coveredByDesk} market={entry.market} />
    </div>
  );
};

// Section wrapper — named label + optional tooltip + tight content slot.
const ReadSection: React.FC<{
  label: string;
  tip?: string;
  accent?: 'gold' | 'default';
  children: React.ReactNode;
}> = ({ label, tip, accent, children }) => (
  <div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.16em', textTransform: 'uppercase',
      color: accent === 'gold' ? palette.goldDeep : palette.ink3,
      marginBottom: 8,
      display: 'flex', alignItems: 'center',
    }}>
      {label}
      {tip && <InfoTip tip={tip} />}
    </div>
    {children}
  </div>
);

// Compact single-line entry for the "other reads" section.
const ReadCompactRow: React.FC<{ entry: DailyReadEntry }> = ({ entry }) => {
  const accent = {
    bull: palette.positive,
    bear: palette.negative,
    mixed: palette.caution,
    neutral: palette.ink3,
  }[entry.accent];
  const modelSummary = entry.notches
    .map((n) => `${n.model} · ${n.symbol}`)
    .join(' + ');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px',
      border: `1px solid ${palette.line}`, background: palette.paper2,
      borderRadius: 2, flexWrap: 'wrap',
    }}>
      <span style={{
        fontFamily: 'var(--serif)', fontSize: 15, color: palette.ink, minWidth: 120,
      }}>{entry.market}</span>
      <span style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.10em', textTransform: 'uppercase',
        padding: '2px 8px', borderRadius: 2,
        background: `${accent}18`, color: accent,
        border: `1px solid ${accent}55`,
        whiteSpace: 'nowrap',
      }}>{entry.headline}</span>
      <span style={{
        fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
        flex: 1, minWidth: 160,
      }}>{modelSummary}</span>
      {entry.coveredByDesk ? (
        <a href={NOTCHES_DESK_URL} target="_blank" rel="noopener noreferrer" style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.10em', textTransform: 'uppercase',
          color: palette.goldDeep, textDecoration: 'none', whiteSpace: 'nowrap',
        }}>See in Notches Desk →</a>
      ) : (
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
          fontStyle: 'italic', whiteSpace: 'nowrap',
        }}>Not currently in Notches Desk</span>
      )}
    </div>
  );
};

// Inline proof under each notch on hero cards.
const NotchProofRow: React.FC<{ notch: OpenNotchViewLike }> = ({ notch }) => {
  const daysLeft = Math.max(0, notch.horizonDays - notch.daysAgo);
  const isResolving = daysLeft === 0;
  const proof = notch.cellHitPct == null
    ? `no track record yet`
    : `Track record on ${notch.symbol}: ${notch.cellHitPct.toFixed(0)}% correct over ${notch.cellN} readings`;
  return (
    <div style={{
      padding: '8px 12px',
      border: `1px solid ${palette.line}`, background: palette.paper,
      borderRadius: 2,
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <ModelChip model={notch.model} />
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 14, color: palette.ink,
        }}>{notch.symbol}</span>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink2, flex: 1, minWidth: 120,
        }}>{notch.reading}</span>
        <span style={{
          fontFamily: 'var(--mono)', fontSize: 10, color: palette.ink3,
          whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center',
        }}>
          <span title={TIP.dayCount} style={{ cursor: 'help' }}>day {notch.daysAgo}/{notch.horizonDays}</span>
          <span style={{ margin: '0 4px' }}>·</span>
          {isResolving
            ? <span title={TIP.resolving} style={{ cursor: 'help' }}>resolving<InfoTip tip={TIP.resolving} /></span>
            : <span title={TIP.daysLeft} style={{ cursor: 'help' }}>{daysLeft}d left<InfoTip tip={TIP.daysLeft} /></span>
          }
        </span>
      </div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3, fontStyle: 'italic',
        display: 'flex', alignItems: 'center',
      }}>
        {proof}{notch.cellHitPct != null && <InfoTip tip={modelTrackRecordTip(notch.model)} />}
      </div>
    </div>
  );
};

// Local structural type — avoids importing the full DailyRead types module.
interface OpenNotchViewLike {
  model: ScorecardModel;
  symbol: string;
  reading: string;
  daysAgo: number;
  horizonDays: number;
  cellHitPct: number | null;
  cellN: number;
}

const DeskCta: React.FC<{ covered: boolean; market: string }> = ({ covered, market }) => {
  if (!covered) {
    return (
      <div style={{
        padding: '10px 14px',
        border: `1px dashed ${palette.line}`,
        background: palette.paper,
        borderRadius: 3,
        fontFamily: 'var(--sans)', fontSize: 12,
        color: palette.ink3, lineHeight: 1.5, fontStyle: 'italic',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
      }}>
        {market} is not currently covered in Notches Desk
        <InfoTip tip={TIP.notchesDesk} />
        <span>. See the reading details above or on the per-model page.</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start' }}>
      <a href={NOTCHES_DESK_URL} target="_blank" rel="noopener noreferrer" style={{
        display: 'inline-block',
        padding: '10px 16px',
        background: palette.ink, color: palette.paper,
        fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        borderRadius: 2, textDecoration: 'none',
      }}>See the full research in Notches Desk →</a>
      <InfoTip tip={TIP.notchesDesk} />
    </div>
  );
};

// ---- Confluence ---------------------------------------------------------

export const ConfluencePill: React.FC<{ live?: boolean }> = ({ live }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 2,
    fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    background: live ? `${palette.gold}30` : `${palette.gold}18`,
    color: palette.goldDeep,
    border: `1px solid ${palette.gold}55`,
  }}>{live ? 'Live confluence' : 'Confluence'}</span>
);

export const ConfluenceBand: React.FC<{ events: ConfluenceEvent[] }> = ({ events }) => {
  if (!events.length) return null;
  const visible = events.slice(0, 8);
  return (
    <div style={{
      marginTop: 32, marginBottom: 16,
      padding: '28px 28px 24px',
      border: `1px solid ${palette.gold}55`,
      background: palette.paper,
      borderRadius: 3,
      boxShadow: `inset 3px 0 0 ${palette.gold}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        <div>
          <span style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.goldDeep,
          }}>Confluence</span>
          <span style={{
            marginLeft: 12, fontFamily: 'var(--serif)', fontSize: 22, color: palette.ink,
          }}>
            {events.length} event{events.length > 1 ? 's' : ''} · ≥2 models aligned
          </span>
        </div>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
          letterSpacing: '0.04em',
        }}>
          within {CONFLUENCE_WINDOW_DAYS}d · same market
        </span>
      </div>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
        margin: '10px 0 20px', lineHeight: 1.5,
      }}>
        Any single notch is a data point; two or more independent models firing on
        the same market inside a week is a regime read. Live-confluence cards
        below have at least one in-flight member.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(360px, 1fr))`,
        gap: 12,
      }}>
        {visible.map((e, i) =>
          <ConfluenceCard key={`${e.market}-${e.endDate}-${i}`} event={e} />)}
      </div>
    </div>
  );
};

const ConfluenceCard: React.FC<{ event: ConfluenceEvent }> = ({ event }) => {
  const dateRange = event.startDate === event.endDate
    ? event.startDate
    : `${event.startDate} → ${event.endDate}`;
  return (
    <div style={{
      border: `1px solid ${palette.line}`,
      background: palette.paper2,
      padding: '16px 18px 14px', borderRadius: 3,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
      }}>
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 18, color: palette.ink,
        }}>{event.market}</span>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.ink3,
        }}>
          {event.distinctModels.length} models · {event.members.length} notches
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {event.hasOpen ? <ConfluencePill live /> : <ConfluencePill />}
        </span>
      </div>
      <div style={{
        fontFamily: 'var(--mono)', fontSize: 11, color: palette.ink3,
        marginBottom: 12,
      }}>{dateRange}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {event.members.map((m, i) => <ConfluenceMemberRow key={i} member={m} />)}
      </div>
    </div>
  );
};

const ConfluenceMemberRow: React.FC<{ member: ConfluenceMember }> = ({ member }) => {
  const imp = implicationFor(member.model, member.tag, member.outcome);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      paddingTop: 8, borderTop: `1px dashed ${palette.line}`,
    }}>
      <ModelChip model={member.model} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            fontFamily: 'var(--serif)', fontSize: 14, color: palette.ink,
          }}>{member.symbol}</span>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, color: palette.ink3,
          }}>{member.date}</span>
          <OutcomeChip outcome={member.outcome} />
        </div>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink2, marginTop: 2,
        }}>
          {member.reading}
          {imp.action && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: palette.goldDeep,
            }}>· {imp.action}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Utility panels + primitives -----------------------------------------

export const LoadingPanel: React.FC = () => (
  <div style={{ marginTop: 48, padding: '40px 0', textAlign: 'center' }}>
    <Body>Loading the scorecard…</Body>
  </div>
);

export const ErrorPanel: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    marginTop: 48, padding: '20px 24px',
    border: `1px solid ${palette.line}`, background: palette.paper2,
    borderRadius: 3,
  }}>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.negative, marginBottom: 8,
    }}>Scorecard unavailable</div>
    <div style={{
      fontFamily: 'var(--mono)', fontSize: 12, color: palette.ink3,
    }}>{message}</div>
  </div>
);

export const MethodologyFootnote: React.FC<{ data: ScorecardPayload }> = ({ data }) => (
  <div style={{
    marginTop: 48, padding: '24px 28px',
    borderTop: `2px solid ${palette.line}`,
  }} className="n2-scorecard-note">
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.6,
      color: palette.ink3, margin: 0,
    }}>
      Window: {Math.round(data.lookbackDays / 30.5)} months for COT / GEX;
      IV history is ~20 months (data begins November 2024).
      COT rows use SPY / QQQ / GLD as the tradable price proxy for ES / NQ / GC.
      GEX rows collapse to first-of-day when the model's regime tag differs
      from the prior day (steady same-regime readings are not news);
      NEAR_GAMMA_FLIP readings imply no directional bet and are not published.
      IV rows fire once on the day IVR crosses INTO an extreme band and
      do not refire while it sits there. AVWAP rows re-anchor daily to
      the trailing 60-day swing low; test events use a 10-day cooldown
      to avoid re-firing at the same level. Losing periods shown in warm
      brick, not hidden.
    </p>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; w?: number }> = ({ children, w }) => (
  <th style={{
    textAlign: 'left', padding: '12px 12px 12px 0',
    fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.ink3,
    width: w, whiteSpace: 'nowrap',
  }}>{children}</th>
);

const Td: React.FC<{
  children?: React.ReactNode; style?: React.CSSProperties; colSpan?: number;
}> = ({ children, style, colSpan }) => (
  <td colSpan={colSpan} style={{
    padding: '14px 12px 14px 0', color: palette.ink, verticalAlign: 'top',
    ...style,
  }}>{children}</td>
);

export const OutcomeChip: React.FC<{ outcome: ScorecardOutcome }> = ({ outcome }) => {
  const base: React.CSSProperties = {
    display: 'inline-block', padding: '3px 10px', borderRadius: 2,
    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
  };
  if (outcome === 'hit')
    return <span style={{ ...base, background: `${palette.positive}18`, color: palette.positive }}>hit</span>;
  if (outcome === 'miss')
    return <span style={{ ...base, background: `${palette.negative}18`, color: palette.negative }}>miss</span>;
  if (outcome === 'neutral')
    return <span style={{ ...base, background: `${palette.line}55`, color: palette.ink3 }}>neutral</span>;
  return <span style={{ ...base, background: `${palette.caution}20`, color: palette.caution }}>open</span>;
};

export const ModelChip: React.FC<{ model: ScorecardModel }> = ({ model }) => (
  <span
    title={modelExplainTip(model)}
    style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 2,
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      border: `1px solid ${palette.line}`, color: palette.ink2,
      cursor: 'help',
    }}
  >{model}</span>
);

const FilterGroup: React.FC<{
  label: string; values: string[]; active: string; onChange: (v: string) => void;
}> = ({ label, values, active, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase', color: palette.ink3,
    }}>{label}</span>
    <div style={{ display: 'flex', gap: 4 }}>
      {values.map(v => (
        <button
          key={v} onClick={() => onChange(v)}
          style={{
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 500,
            padding: '5px 10px', border: `1px solid ${v === active ? palette.ink : palette.line}`,
            background: v === active ? palette.ink : 'transparent',
            color: v === active ? palette.paper : palette.ink2,
            borderRadius: 2, cursor: 'pointer',
          }}
        >{v}</button>
      ))}
    </div>
  </div>
);

// Also re-export helpers callers commonly need together with the components.
export { detectConfluence };
